/**
 * Conditional Discount Upsell — Cart Drawer Modal + Slider
 */
(function () {
  'use strict';

  const SETTINGS_EL_ID = 'conditional-upsell-settings';
  const MODAL_EL_ID = 'ConditionalUpsellModal';
  const SLIDER_FILLED_SELECTOR = '[data-upsell-filled]';
  const SLIDER_EMPTY_SELECTOR = '[data-upsell-empty]';
  const MAX_PRODUCTS = 8;
  const DRAWER_TRANSITION_MS = 300;
  const SUPPRESS_OPEN = Symbol('condUpsell.suppressOpen');

  function setSuppressDrawerOpen(value) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer[SUPPRESS_OPEN] = value;
  }

  let settings = null;
  let suppressNextCheck = false;
  let cascadeClosing = false;
  let drawerOpenObserverInstalled = false;

  // ─── 1. Boot ─────────────────────────────────────────────

  function readSettings() {
    const el = document.getElementById(SETTINGS_EL_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function init() {
    settings = readSettings();
    if (!settings) return;

    defineModalElement();
    patchCartDrawerOpen();
    subscribeToCartUpdate();
    installDrawerOpenObserver();

    if (settings.sliderEnabled) {
      window.addEventListener('co2.cart.initialized', refreshSlider);
      if (typeof co2 !== 'undefined') refreshSlider();
    }
  }

  // ─── 1a. Custom element ──────────────────────────────────

  function defineModalElement() {
    if (typeof ModalDialog === 'undefined') return;
    if (customElements.get('conditional-upsell-modal')) return;

    class ConditionalUpsellModal extends ModalDialog {
      connectedCallback() {
        if (this.moved) return;
        this.moved = true;
        document.body.appendChild(this);
      }

      hide() {
        super.hide();
        const slot = this.querySelector('.conditional-upsell-modal__products');
        if (slot) slot.innerHTML = '';

        if (cascadeClosing) {
          cascadeClosing = false;
          return;
        }
        openCartDrawer();
      }
    }

    customElements.define('conditional-upsell-modal', ConditionalUpsellModal);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ─── 2. Drawer auto-open suppression ─────────────────────

  function patchCartDrawerOpen() {
    if (!window.customElements) return;

    window.customElements.whenDefined('cart-drawer').then(() => {
      const CartDrawerCtor = window.customElements.get('cart-drawer');
      if (!CartDrawerCtor || CartDrawerCtor.__condUpsellPatched) return;

      const proto = CartDrawerCtor.prototype;
      const originalOpen = proto.open;

      proto.open = function patchedOpen(triggeredBy) {
        if (this[SUPPRESS_OPEN]) return;
        return originalOpen.call(this, triggeredBy);
      };

      CartDrawerCtor.__condUpsellPatched = true;
    });
  }

  // ─── 3. cartUpdate subscriber ────────────────────────────

  function subscribeToCartUpdate() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    if (PUB_SUB_EVENTS.cartRerendered) {
      subscribe(PUB_SUB_EVENTS.cartRerendered, refreshSlider);
    }

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      refreshSlider();

      if (!settings.modalEnabled) return;
      if (!event || event.source !== 'product-form') return;

      if (suppressNextCheck) {
        suppressNextCheck = false;
        setSuppressDrawerOpen(false);
        // Mark cascade so the modalClosed handler doesn't manually open the
        // drawer — renderContents() will open it naturally with the new cart.
        cascadeClosing = true;
        closeModal();
        return;
      }

      setSuppressDrawerOpen(true);
      checkAndShowModal();
    });
  }

  async function checkAndShowModal() {
    const result = await getCandidates();

    if (!result || result.items.length === 0) {
      openCartDrawer();
      return;
    }

    renderModal(result.items);
    openModal();
  }

  // ─── 4. Candidate fetch ──────────────────────────────────
  async function getCandidates() {
    if (typeof co2 === 'undefined') return null;

    try {
      const products = await co2.unredeemedCondDiscountProducts();
      if (!Array.isArray(products)) return { items: [], total: 0 };
      return { items: products.slice(0, MAX_PRODUCTS), total: products.length };
    } catch (e) {
      return { items: [], total: 0 };
    }
  }

  // ─── 5. Slider tree (shared between modal + drawer slider) ───

  function buildSliderTree(candidates, headerText) {
    const slider = document.createElement('simple-upsell-slider');
    slider.className = 'simple-upsell block bg-[#ede9e6] mt-6 py-4 pl-6 pr-0 overflow-hidden';
    slider.setAttribute('data-nosnippet', '');
    applySliderSettings(slider);

    // Header (title + custom prev/next nav)
    const header = document.createElement('div');
    header.className = 'simple-upsell__header flex items-center justify-between gap-3 pr-6 mb-3';

    const title = document.createElement('div');
    title.className = 'simple-upsell__title font-bold';
    title.textContent = headerText || '';
    header.appendChild(title);

    const nav = document.createElement('div');
    nav.className = 'simple-upsell__nav hidden md:flex items-center gap-2';
    nav.setAttribute('data-simple-upsell-nav', '');

    nav.appendChild(buildNavButton('prev'));
    nav.appendChild(buildNavButton('next'));
    header.appendChild(nav);
    slider.appendChild(header);

    // Track
    const track = document.createElement('div');
    track.className = 'simple-upsell__track';
    track.setAttribute('data-simple-upsell-track', '');
    candidates.forEach((c) => track.appendChild(buildCard(c)));
    slider.appendChild(track);

    return slider;
  }

  function applySliderSettings(el) {
    const map = {
      sliderDragDesktop: 'sliderDragDesktop',
      sliderSwipeMobile: 'sliderSwipeMobile',
      sliderMobileMode: 'sliderMobileMode',
      sliderAutoGroup: 'sliderAutoGroup',
      sliderStepDesktop: 'sliderStepDesktop',
      sliderStepTablet: 'sliderStepTablet',
      sliderStepMobile: 'sliderStepMobile',
      sliderShowArrows: 'sliderShowArrows',
      sliderShowDots: 'sliderShowDots',
      sliderTransition: 'sliderTransition',
      sliderMobileFriction: 'sliderMobileFriction',
    };
    Object.keys(map).forEach((key) => {
      if (settings[key] !== undefined && settings[key] !== null) {
        el.dataset[map[key]] = String(settings[key]);
      }
    });
  }

  function buildNavButton(direction) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'simple-upsell__nav-btn w-7 h-7 inline-flex items-center justify-center rounded-full bg-white text-neutral-900 border-0 cursor-pointer hover:opacity-80 disabled:opacity-30 disabled:cursor-default transition-opacity';
    btn.setAttribute(direction === 'prev' ? 'data-simple-upsell-prev' : 'data-simple-upsell-next', '');
    const label = direction === 'prev'
      ? (settings.navPrevLabel || 'Previous')
      : (settings.navNextLabel || 'Next');
    btn.setAttribute('aria-label', label);
    const points = direction === 'prev' ? '15 18 9 12 15 6' : '9 18 15 12 9 6';
    btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="' + points + '"></polyline></svg>';
    return btn;
  }

  function buildCard(candidate) {
    const card = document.createElement('div');
    card.className = 'simple-upsell__card flex-none w-[160px] md:w-[180px] mr-3 flex flex-col';

    const url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/') + 'products/' + candidate.handle;

    const variant = pickCheapestVariant(candidate.variants);
    const variantImage = variant && variant.image ? variant.image : null;
    const cardImage = variantImage || candidate.image || '';
    const productUrlWithVariant = variant ? url + '?variant=' + variant.id : url;

    // Image link
    const imgLink = document.createElement('a');
    imgLink.href = productUrlWithVariant;
    imgLink.className = 'simple-upsell__card-link block';
    if (cardImage) {
      const img = document.createElement('img');
      img.className = 'simple-upsell__card-image block w-full aspect-square object-cover rounded-sm';
      img.src = cardImage;
      img.alt = candidate.title || '';
      img.loading = 'lazy';
      img.width = 260;
      img.height = 260;
      imgLink.appendChild(img);
    }
    card.appendChild(imgLink);

    // Title link
    const titleLink = document.createElement('a');
    titleLink.href = productUrlWithVariant;
    titleLink.className = 'simple-upsell__card-title block mt-1.5 mb-0.5 text-[11px] leading-[1.3] text-inherit no-underline line-clamp-2';
    titleLink.textContent = candidate.title || '';
    card.appendChild(titleLink);

    const price = variant ? variant.price : 0;
    const discountedPrice = Math.round(price - price * candidate.condPerc / 100);
    const priceOuter = document.createElement('div');
    priceOuter.className = 'simple-upsell__card-price text-[11px] flex items-center flex-wrap gap-x-1 gap-y-0.5 mb-1.5';
    priceOuter.appendChild(buildPriceBlock({
      price: price,
      discountedPrice: discountedPrice,
      condPerc: candidate.condPerc,
    }));
    card.appendChild(priceOuter);

    // CTA — opens the ATC modal via its global click handler
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'simple-upsell__card-btn mt-auto w-full px-2 text-[10px] font-semibold tracking-[0.5px] uppercase text-white text-center bg-[#067301] hover:opacity-85 transition-opacity cursor-pointer border-0 rounded-[var(--button-border-radius,0px)] leading-[30px]';
    btn.textContent = settings.btnText || '';
    btn.setAttribute('data-atc-modal-trigger', '');
    btn.setAttribute('data-product-url', url);
    if (variant) btn.setAttribute('data-variant-id', String(variant.id));
    btn.setAttribute('aria-haspopup', 'dialog');
    btn.addEventListener('click', () => {
      suppressNextCheck = true;
    });
    card.appendChild(btn);

    return card;
  }

  function buildPriceBlock(opts) {
    const wrap = document.createElement('div');
    wrap.className = 'price price--simple-upsell discounted';

    const container = document.createElement('div');
    container.className = 'price__container';

    const regular = document.createElement('div');
    regular.className = 'price__regular';
    container.appendChild(regular);

    const sale = document.createElement('div');
    sale.className = 'price__sale';
    container.appendChild(sale);

    const list = document.createElement('div');
    list.className = 'price-list';

    const discPrice = document.createElement('span');
    discPrice.className = 'price';
    discPrice.innerHTML = formatCents(opts.discountedPrice);
    list.appendChild(discPrice);

    const comparePrice = document.createElement('span');
    comparePrice.className = 'price price--compare';
    comparePrice.innerHTML = formatCents(opts.price);
    list.appendChild(comparePrice);

    container.appendChild(list);
    wrap.appendChild(container);

    const badge = document.createElement('span');
    badge.className = 'datora discount-badge';
    badge.innerHTML = '<b>-' + opts.condPerc + '%</b>';
    wrap.appendChild(badge);

    return wrap;
  }

  function formatCents(cents) {
    if (typeof formatPrice === 'function') return formatPrice(cents);
    return (cents / 100).toFixed(2) + ' €';
  }

  function pickCheapestVariant(variants) {
    if (!Array.isArray(variants) || variants.length === 0) return null;
    let cheapest = variants[0];
    for (let i = 1; i < variants.length; i++) {
      if (variants[i].price < cheapest.price) cheapest = variants[i];
    }
    return cheapest;
  }

  // ─── 6. Modal render ─────────────────────────────────────

  function renderModal(candidates) {
    const modal = document.getElementById(MODAL_EL_ID);
    if (!modal) return;

    const headerEl = modal.querySelector('.conditional-upsell-modal__header');
    if (headerEl) {
      const maxPerc = candidates.reduce((m, c) => (c.condPerc > m ? c.condPerc : m), 0);
      headerEl.textContent = (settings.modalHeaderText || '').replace('[[PERCENT]]', String(maxPerc));
    }

    const slot = modal.querySelector('.conditional-upsell-modal__products');
    if (!slot) return;

    slot.innerHTML = '';
    slot.appendChild(buildSliderTree(candidates, ''));
  }

  // ─── 7. Modal open / close — thin wrappers around <conditional-upsell-modal> ───

  function openModal() {
    const modal = document.getElementById(MODAL_EL_ID);
    if (!modal || typeof modal.show !== 'function') return;
    modal.show();
  }

  function closeModal() {
    const modal = document.getElementById(MODAL_EL_ID);
    if (!modal || typeof modal.hide !== 'function' || !modal.hasAttribute('open')) return;
    modal.hide();
  }

  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    // Clear the suppress flag so this open() call is allowed through.
    drawer[SUPPRESS_OPEN] = false;
    requestAnimationFrame(() => {
      drawer.open();
    });
  }

  // ─── 8. Drawer slider render ─────────────────────────────

  async function refreshSlider() {
    if (!settings.sliderEnabled) return;
    const result = await getCandidates();
    if (result === null) return;
    renderSlider(result.items, result.total);
  }

  function renderSlider(candidates, totalRemaining) {
    const cart = document.querySelector('cart-drawer');
    if (!cart) return;

    const isEmpty = cart.classList.contains('is-empty');
    const targetSel = isEmpty ? SLIDER_EMPTY_SELECTOR : SLIDER_FILLED_SELECTOR;
    const target = cart.querySelector(targetSel);
    const otherTarget = cart.querySelector(isEmpty ? SLIDER_FILLED_SELECTOR : SLIDER_EMPTY_SELECTOR);

    // Clear the unused slot — disconnects any prior <simple-upsell-slider> there
    if (otherTarget) {
      otherTarget.innerHTML = '';
      otherTarget.hidden = true;
    }

    if (!target) return;

    if (!candidates.length) {
      target.innerHTML = '';
      target.hidden = true;
      return;
    }

    target.innerHTML = '';
    target.hidden = false;

    const headerText = (settings.sliderHeaderText || '').replace('[[COUNT]]', String(totalRemaining));
    target.appendChild(buildSliderTree(candidates, headerText));

    requestAnimationFrame(() => {
      const drawerEl = document.querySelector('cart-drawer.active');
      if (drawerEl) requestAnimationFrame(scrollSliderIntoView);
    });
  }

  // ─── 9. Auto-scroll to slider when drawer opens ──────────

  function installDrawerOpenObserver() {
    if (drawerOpenObserverInstalled) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName !== 'class') continue;
        if (drawer.classList.contains('active')) {
          setTimeout(scrollSliderIntoView, DRAWER_TRANSITION_MS);
        }
      }
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    drawerOpenObserverInstalled = true;
  }

  function scrollSliderIntoView() {
    const cart = document.querySelector('cart-drawer');
    if (!cart) return;
    const slider = cart.querySelector(SLIDER_FILLED_SELECTOR + ' simple-upsell-slider');
    if (!slider || !slider.offsetParent) return;
    slider.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }
})();
